import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { HERO_IMAGE, STUDIO } from "../data/site";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-16 px-5 md:px-10 lg:px-16 overflow-hidden"
    >
      {/* Background blobs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-lime/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-hot/20 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-grape/20 blur-[120px]" />
      </div>

      <div className="relative z-10 grid grid-cols-12 gap-6 items-center min-h-[85vh]">
        {/* Left: Headline */}
        <div className="col-span-12 lg:col-span-8 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur rounded-full px-4 py-2 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-lime animate-pulse" />
            <span className="font-body text-xs tracking-widest uppercase text-cream/80">{STUDIO.address}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="font-display font-extrabold leading-[0.88] tracking-tighter text-cream"
            style={{ fontSize: "clamp(3.5rem, 12vw, 12rem)" }}
          >
            <span className="block">onde a <span className="text-lime">música</span></span>
            <span className="block">vira <span className="relative inline-block isolate px-4">
              <span aria-hidden className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[88%] bg-lime brush" style={{zIndex: 0}} />
              <span className="relative italic text-ink" style={{zIndex: 1}}>carreira</span>
            </span><span className="text-hot">.</span></span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="font-body text-lg md:text-xl text-cream/70 max-w-2xl mt-8 leading-relaxed"
          >
            Estúdio de gravação, imagem, videoclips e uma agência criativa que leva a tua música para onde ela merece chegar.
            <span className="font-hand text-3xl text-hot ml-2">zero ruído, muita estratégia.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <a
              href="#booking"
              data-testid="hero-book-session-button"
              className="group inline-flex items-center gap-2 bg-lime text-ink font-display font-bold text-base md:text-lg px-7 py-4 rounded-full hover:bg-hot hover:text-cream transition-colors"
            >
              Agendar sessão
              <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              href="#portfolio"
              data-testid="hero-services-button"
              className="group inline-flex items-center gap-2 border border-cream/30 text-cream font-display font-bold text-base md:text-lg px-7 py-4 rounded-full hover:bg-cream hover:text-ink transition-colors"
            >
              Ver trabalhos
              <Sparkles size={18} className="group-hover:rotate-12 transition-transform" />
            </a>
          </motion.div>
        </div>

        {/* Right: Visual card with sticker */}
        <div className="col-span-12 lg:col-span-4 relative lg:mt-24">
          <motion.div
            initial={{ opacity: 0, rotate: -4, y: 40 }}
            animate={{ opacity: 1, rotate: -3, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="relative aspect-[4/5] w-full max-w-md mx-auto rounded-3xl overflow-hidden border-2 border-cream/10 shadow-2xl"
          >
            <img
              src={HERO_IMAGE}
              alt="Microfone profissional TAKA TAKA"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Spinning sticker */}
          <div className="absolute -top-6 -right-2 md:-right-6 w-32 h-32 spin-slow">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <path id="circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <circle cx="50" cy="50" r="48" fill="#FF3D7F" />
              <text fill="#0A0A0A" fontFamily="Anton, sans-serif" fontSize="11" fontWeight="800">
                <textPath href="#circle">· AGÊNCIA · MÚSICA · IMAGEM · ARTISTAS </textPath>
              </text>
              <text x="50" y="56" textAnchor="middle" fill="#0A0A0A" fontSize="18" fontFamily="Anton" fontWeight="900">✦</text>
            </svg>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="relative z-10 mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/10 pt-8"
      >
        {[
          { k: "400+", v: "projetos entregues" },
          { k: "140+", v: "artistas em rotação" },
          { k: "1M+", v: "streams geridos" },
          { k: "★ 5.0", v: "avaliação média" },
        ].map((s, i) => (
          <div key={i} className="flex flex-col">
            <span className="font-display font-extrabold text-4xl md:text-5xl text-cream">{s.k}</span>
            <span className="font-body text-xs uppercase tracking-widest text-cream/50 mt-1">{s.v}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
